import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, Image as ImageIcon, RefreshCw, Wifi, WifiOff, Camera, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { getAbsoluteImageUrl } from '../utils/urlUtils';
import { retryUploadOperation } from '../utils/networkResilience';
import { parseBackendError, getErrorMessage as getStructuredErrorMessage, ERROR_TYPES } from '../utils/errorHandling';
import {
  startImageLoadTimer,
  endImageLoadTimer,
  recordOperationAttempt,
  recordUserExperience,
  recordImageError
} from '../utils/performanceMonitor';
import CameraModal from './CameraModal';

const AssetImageUpload = ({
  images = [],
  onImagesChange,
  maxImages = 5,
  disabled = false,
  className = ''
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, active: false });
  const [dragActive, setDragActive] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});
  const [retryAttempts, setRetryAttempts] = useState({});
  const [processingQueue, setProcessingQueue] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkQuality, setNetworkQuality] = useState('good'); // good, slow, poor
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(false);
  const fileInputRef = useRef(null);
  const networkCheckRef = useRef(null);

  // Check camera support on component mount
  React.useEffect(() => {
    const checkCameraSupport = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(device => device.kind === 'videoinput');
        setCameraSupported(hasCamera && !!navigator.mediaDevices.getUserMedia);
      } catch (error) {
        console.log('Camera support check failed:', error);
        setCameraSupported(false);
      }
    };

    checkCameraSupport();
  }, []);

  // Enhanced network monitoring with quality detection
  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkNetworkQuality();
      processOfflineQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkQuality('offline');
    };

    // Real-time network quality monitoring
    const checkNetworkQuality = async () => {
      if (!navigator.onLine) {
        setNetworkQuality('offline');
        return;
      }

      try {
        const startTime = Date.now();
        const response = await fetch(window.location.origin + '/favicon.ico', {
          method: 'HEAD',
          cache: 'no-store'
        });
        const endTime = Date.now();
        const responseTime = endTime - startTime;

        if (response.ok) {
          if (responseTime < 500) {
            setNetworkQuality('good');
          } else if (responseTime < 2000) {
            setNetworkQuality('slow');
          } else {
            setNetworkQuality('poor');
          }
        } else {
          setNetworkQuality('poor');
        }
      } catch (error) {
        setNetworkQuality('poor');
      }
    };

    // Process queued uploads when coming back online
    const processOfflineQueue = async () => {
      if (offlineQueue.length === 0) return;

      toast.success(`Processing ${offlineQueue.length} queued upload${offlineQueue.length > 1 ? 's' : ''}...`);

      const queuedUploads = [...offlineQueue];
      setOfflineQueue([]);

      for (const queuedUpload of queuedUploads) {
        try {
          await queuedUpload.operation();
          toast.success(`Uploaded ${queuedUpload.filename}`);
        } catch (error) {
          console.error('Failed to process queued upload:', error);
          toast.error(`Failed to upload ${queuedUpload.filename}`);
        }
      }
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic network quality check
    networkCheckRef.current = setInterval(checkNetworkQuality, 30000); // Check every 30 seconds

    // Initial network quality check
    if (navigator.onLine) {
      checkNetworkQuality();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (networkCheckRef.current) {
        clearInterval(networkCheckRef.current);
      }
    };
  }, [offlineQueue]);

  // File validation constants matching backend requirements
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE = 64 * 1024 * 1024; // 64MB
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];



  const getErrorMessage = (errorType, details = {}) => {
    const errorMessages = {
      FILE_TYPE: `Invalid file type. Only JPEG, PNG, and WebP images are supported.`,
      INVALID_FILE_TYPE: `Invalid file type. Only JPEG, PNG, and WebP images are supported.`,
      FILE_SIZE: `File too large. Maximum size is 5MB. Your file is ${details.actualSize || 'unknown size'}.`,
      FILE_TOO_LARGE: `File too large. Maximum size is 5MB.`,
      FILE_EXTENSION: `Invalid file extension. Supported formats: ${ALLOWED_EXTENSIONS.join(', ')}`,
      INVALID_FILE_EXTENSION: `Invalid file extension. Supported formats: ${ALLOWED_EXTENSIONS.join(', ')}`,
      NETWORK_ERROR: `Network connection failed. Please check your internet connection and try again.`,
      SERVER_ERROR: `Server error occurred. Please try again in a few moments.`,
      PERMISSION_ERROR: `You don't have permission to upload images. Please contact your administrator.`,
      QUOTA_EXCEEDED: `Maximum ${maxImages} images allowed per asset. Please remove some images first.`,
      PROCESSING_ERROR: `Failed to process the image file. The file may be corrupted.`,
      UPLOAD_TIMEOUT: `Upload timed out. Please check your connection and try again.`,
      VALIDATION_ERROR: `Invalid request. Please check your input and try again.`,
      ASSET_NOT_FOUND: `Asset not found. Please refresh the page and try again.`,
      GENERIC_ERROR: `An unexpected error occurred. Please try again.`
    };

    return errorMessages[errorType] || errorMessages.GENERIC_ERROR;
  };

  const validateFile = (file) => {
    const errors = [];

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      errors.push({ type: 'FILE_TYPE', message: getErrorMessage('FILE_TYPE') });
    }

    // Check file extension as additional validation
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      errors.push({ type: 'FILE_EXTENSION', message: getErrorMessage('FILE_EXTENSION') });
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const actualSize = `${(file.size / (1024 * 1024)).toFixed(2)}MB`;
      errors.push({
        type: 'FILE_SIZE',
        message: getErrorMessage('FILE_SIZE', { actualSize })
      });
    }

    return errors;
  };

  const retryUpload = async (imageId) => {
    const image = images.find(img => img.id === imageId);
    if (!image || !image.file) return;

    const currentAttempts = retryAttempts[imageId] || 0;
    if (currentAttempts >= 3) {
      toast.error(`Maximum retry attempts reached for ${image.filename}`);
      return;
    }

    setRetryAttempts(prev => ({ ...prev, [imageId]: currentAttempts + 1 }));
    setUploadErrors(prev => ({ ...prev, [imageId]: null }));

    try {
      await processFile(image.file, imageId);
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  const uploadToServer = useCallback(async (file, assetId, onProgress, timeout = 30000) => {
    const formData = new FormData();
    formData.append('file', file);

    const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    // Start performance monitoring for upload operation
    const uploadTimerId = startImageLoadTimer(`upload_${file.name}`, {
      operationType: 'upload',
      fileSize: file.size,
      fileName: file.name,
      networkQuality: networkQuality
    });

    // Direct upload operation with configurable timeout
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            // Record successful upload operation
            endImageLoadTimer(uploadTimerId, {
              success: true,
              cached: false,
              fileSize: file.size,
              networkQuality: networkQuality
            });
            recordOperationAttempt('upload', true);
            recordUserExperience({
              timeToFirstByte: performance.now() - uploadTimerId.split('_')[1],
              timeToInteractive: performance.now() - uploadTimerId.split('_')[1]
            });
            resolve(response);
          } catch (error) {
            // Record parsing error
            endImageLoadTimer(uploadTimerId, { success: false, error: 'Invalid server response' });
            recordOperationAttempt('upload', false);
            recordImageError(new Error('Invalid server response'), {
              operationType: 'upload',
              fileName: file.name
            });
            reject(new Error('Invalid server response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            const error = new Error('Upload failed');
            error.response = { status: xhr.status, data: errorResponse };
            // Record upload failure
            endImageLoadTimer(uploadTimerId, { success: false, error: `HTTP ${xhr.status}` });
            recordOperationAttempt('upload', false);
            recordImageError(error, {
              operationType: 'upload',
              fileName: file.name,
              httpStatus: xhr.status
            });
            reject(error);
          } catch (parseError) {
            const error = new Error(`Upload failed with status ${xhr.status}`);
            error.response = { status: xhr.status };
            // Record upload failure
            endImageLoadTimer(uploadTimerId, { success: false, error: `HTTP ${xhr.status}` });
            recordOperationAttempt('upload', false);
            recordImageError(error, {
              operationType: 'upload',
              fileName: file.name,
              httpStatus: xhr.status
            });
            reject(error);
          }
        }
      });

      xhr.addEventListener('error', () => {
        // Record network error
        endImageLoadTimer(uploadTimerId, { success: false, error: 'Network error' });
        recordOperationAttempt('upload', false);
        recordImageError(new Error('Network error occurred'), {
          operationType: 'upload',
          fileName: file.name,
          networkQuality: networkQuality
        });
        reject(new Error('Network error occurred'));
      });

      xhr.addEventListener('timeout', () => {
        // Record timeout error
        endImageLoadTimer(uploadTimerId, { success: false, error: 'Upload timeout' });
        recordOperationAttempt('upload', false);
        recordImageError(new Error('Upload timed out'), {
          operationType: 'upload',
          fileName: file.name,
          timeout: timeout,
          networkQuality: networkQuality
        });
        reject(new Error('Upload timed out'));
      });

      xhr.timeout = timeout; // Use configurable timeout based on network quality
      xhr.withCredentials = true; // Include cookies for authentication

      xhr.open('POST', `${API_BASE}/api/assets/${assetId}/images`);
      xhr.send(formData);
    });
  }, []);

  const processFile = useCallback(async (file, existingId = null, assetId = null) => {
    const fileId = existingId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Clear any existing errors for this file
      setUploadErrors(prev => ({ ...prev, [fileId]: null }));

      // Set initial progress
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      // Create image preview immediately for better UX
      const reader = new FileReader();
      const imageData = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Create base image object
      const imageObject = {
        id: fileId,
        file: file,
        filename: file.name,
        original_filename: file.name,
        content_type: file.type,
        size: file.size,
        preview: imageData,
        temporary: !assetId, // Only temporary if no direct upload
        uploaded_at: new Date().toISOString(),
        status: 'processing'
      };

      // Connectivity-aware upload strategy
      if (assetId) {
        if (isOnline) {
          // Online - attempt direct upload with network quality consideration
          try {
            // Adjust timeout based on network quality
            const uploadTimeout = networkQuality === 'good' ? 30000 :
              networkQuality === 'slow' ? 60000 : 90000;

            const serverResponse = await uploadToServer(file, assetId, (progress) => {
              setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
            }, uploadTimeout);

            // Update with server response
            imageObject.id = serverResponse.id;
            imageObject.url = serverResponse.url;
            imageObject.uploaded_at = serverResponse.uploaded_at;
            imageObject.temporary = false;
            imageObject.status = 'completed';

          } catch (uploadError) {
            const parsedError = parseBackendError(uploadError);

            // Check if error is network-related and should be queued
            if (parsedError.type === ERROR_TYPES.NETWORK_ERROR ||
              parsedError.type === ERROR_TYPES.CONNECTION_TIMEOUT) {

              // Queue for offline processing
              const queuedUpload = {
                id: fileId,
                filename: file.name,
                operation: () => uploadToServer(file, assetId, () => { }),
                timestamp: Date.now()
              };

              setOfflineQueue(prev => [...prev, queuedUpload]);
              imageObject.status = 'queued';
              imageObject.queuedForUpload = true;

              toast.info(`${file.name} queued for upload when connection improves`);
            } else {
              // Non-network error - show error immediately
              setUploadErrors(prev => ({
                ...prev,
                [fileId]: { type: parsedError.type, message: parsedError.message }
              }));

              toast.error(`${file.name}: ${parsedError.message}`);

              imageObject.status = 'error';
              imageObject.error = parsedError;

              throw uploadError;
            }
          }
        } else {
          // Offline - queue upload for when connection is restored
          const queuedUpload = {
            id: fileId,
            filename: file.name,
            operation: () => uploadToServer(file, assetId, () => { }),
            timestamp: Date.now()
          };

          setOfflineQueue(prev => [...prev, queuedUpload]);
          imageObject.status = 'queued';
          imageObject.queuedForUpload = true;

          toast.info(`${file.name} queued for upload when online`);
        }
      } else {
        // Local preview mode - simulate progress for better UX
        const progressSteps = [25, 50, 75, 100];
        for (const step of progressSteps) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setUploadProgress(prev => ({ ...prev, [fileId]: step }));
        }
        imageObject.status = 'completed';
      }

      return imageObject;

    } catch (error) {
      console.error('Error processing file:', file.name, error);

      // Use parseBackendError utility for consistent error handling
      const errorInfo = parseBackendError(error);

      setUploadErrors(prev => ({
        ...prev,
        [fileId]: { type: errorInfo.type, message: errorInfo.message }
      }));

      // Clean up progress tracking on error
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });

      throw error;
    }
  }, [isOnline]);

  const processFiles = useCallback(async (files) => {
    const fileArray = Array.from(files);

    // Check total image count
    if (images.length + fileArray.length > maxImages) {
      const errorMessage = getErrorMessage('QUOTA_EXCEEDED');
      toast.error(errorMessage);
      return;
    }

    const validFiles = [];
    const validationErrors = [];

    // Validate each file
    fileArray.forEach(file => {
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        fileErrors.forEach(error => {
          validationErrors.push(`${file.name}: ${error.message}`);
        });
      } else {
        validFiles.push(file);
      }
    });

    // Show validation errors
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    if (validFiles.length === 0) return;

    // Initialize batch progress tracking
    setBatchProgress({ current: 0, total: validFiles.length, active: true });

    const newImages = [];
    const failedUploads = [];
    const BATCH_SIZE = networkQuality === 'good' ? 3 : networkQuality === 'slow' ? 2 : 1;

    try {
      // Process files in batches to prevent overwhelming server
      for (let i = 0; i < validFiles.length; i += BATCH_SIZE) {
        const batch = validFiles.slice(i, i + BATCH_SIZE);

        // Process batch concurrently but with limited concurrency
        const batchPromises = batch.map(async (file, batchIndex) => {
          const overallIndex = i + batchIndex;

          try {
            // Update batch progress
            setBatchProgress(prev => ({ ...prev, current: overallIndex }));

            const imageObject = await processFile(file);

            // Update batch progress after completion
            setBatchProgress(prev => ({ ...prev, current: overallIndex + 1 }));

            return { success: true, imageObject, filename: file.name };
          } catch (error) {
            console.error(`Failed to process ${file.name}:`, error);
            setBatchProgress(prev => ({ ...prev, current: overallIndex + 1 }));
            return { success: false, error, filename: file.name };
          }
        });

        // Wait for current batch to complete before starting next batch
        const batchResults = await Promise.allSettled(batchPromises);

        // Process batch results
        batchResults.forEach((result, batchIndex) => {
          if (result.status === 'fulfilled') {
            const { success, imageObject, filename, error } = result.value;
            if (success) {
              newImages.push(imageObject);
            } else {
              failedUploads.push(filename);
            }
          } else {
            // Promise was rejected
            const filename = (batch[batchIndex] && batch[batchIndex].name) || 'Unknown file';
            failedUploads.push(filename);
          }
        });

        // Add delay between batches to prevent server overload
        if (i + BATCH_SIZE < validFiles.length) {
          const delay = networkQuality === 'good' ? 500 : networkQuality === 'slow' ? 1000 : 2000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // Update parent component with successfully processed images
      if (newImages.length > 0) {
        onImagesChange([...images, ...newImages]);

        let successMessage = `${newImages.length} image${newImages.length > 1 ? 's' : ''} processed successfully`;
        if (failedUploads.length > 0) {
          successMessage += `, ${failedUploads.length} failed`;
        }

        toast.success(successMessage);
      }

      // Show summary of failed uploads
      if (failedUploads.length > 0 && newImages.length === 0) {
        toast.error(`Failed to process ${failedUploads.length} image${failedUploads.length > 1 ? 's' : ''}`);
      }

    } catch (error) {
      console.error('Batch processing error:', error);
      toast.error('An error occurred during batch processing');
    } finally {
      // Reset batch progress
      setBatchProgress({ current: 0, total: 0, active: false });

      // Clean up individual progress indicators after a delay
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          newImages.forEach(img => delete newProgress[img.id]);
          return newProgress;
        });
      }, 2000);
    }
  }, [images, maxImages, onImagesChange, networkQuality]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    onImagesChange(updatedImages);

    // Clean up progress tracking
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[imageId];
      return newProgress;
    });

    toast.success('Image removed');
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraCapture = (file) => {
    if (disabled) return;
    
    // Check if we can add more images
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Process the captured photo
    processFiles([file]);
    setShowCameraModal(false);
  };

  const openCameraModal = () => {
    if (!disabled && cameraSupported) {
      setShowCameraModal(true);
    } else if (!cameraSupported) {
      toast.error('Camera not supported on this device');
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Options */}
      <div className="space-y-4">
        {/* Choice Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Camera Option */}
          <button
            onClick={openCameraModal}
            disabled={disabled || !cameraSupported}
            className={`
              flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors
              ${disabled || !cameraSupported
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed text-gray-400'
                : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50 text-gray-600 hover:text-cyan-600'
              }
            `}
          >
            <Camera size={32} className="mb-2" />
            <span className="text-sm font-medium">
              {cameraSupported ? 'Take Photo' : 'Camera Not Available'}
            </span>
            <span className="text-xs mt-1 text-center">
              {cameraSupported ? 'Use your device camera' : 'Camera not supported'}
            </span>
          </button>

          {/* Gallery Option */}
          <button
            onClick={openFileDialog}
            disabled={disabled}
            className={`
              flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors
              ${disabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed text-gray-400'
                : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50 text-gray-600 hover:text-cyan-600'
              }
            `}
          >
            <FolderOpen size={32} className="mb-2" />
            <span className="text-sm font-medium">Choose from Gallery</span>
            <span className="text-xs mt-1 text-center">
              Select from your device
            </span>
          </button>
        </div>

        {/* Drag and Drop Area */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-4 text-center transition-colors
            ${dragActive
              ? 'border-cyan-500 bg-cyan-50'
              : disabled
                ? 'border-gray-200 bg-gray-50'
                : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50'
            }
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />

          <div className="flex flex-col items-center">
            <Upload
              size={24}
              className={`mb-2 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
            />
            <span className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
              {dragActive
                ? 'Drop images here'
                : 'Or drag and drop images here'
              }
            </span>
            <span className={`text-xs mt-1 ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
              JPEG, PNG, WebP (Max 5MB each, {maxImages} images max)
            </span>
            {images.length > 0 && (
              <span className="text-xs text-cyan-600 mt-1">
                {images.length} of {maxImages} images uploaded
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Network Status Indicator */}
      {!isOnline ? (
        <div className="flex items-center justify-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <WifiOff size={16} className="text-red-600 mr-2" />
          <span className="text-sm text-red-700 font-medium">
            No internet connection. {offlineQueue.length > 0 && `${offlineQueue.length} upload${offlineQueue.length > 1 ? 's' : ''} queued.`}
          </span>
        </div>
      ) : networkQuality === 'poor' ? (
        <div className="flex items-center justify-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <Wifi size={16} className="text-yellow-600 mr-2" />
          <span className="text-sm text-yellow-700 font-medium">
            Poor connection detected. Uploads may be slower than usual.
          </span>
        </div>
      ) : networkQuality === 'slow' ? (
        <div className="flex items-center justify-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Wifi size={16} className="text-blue-600 mr-2" />
          <span className="text-sm text-blue-700 font-medium">
            Slow connection detected. Using extended timeouts.
          </span>
        </div>
      ) : null}

      {/* Offline Queue Status */}
      {offlineQueue.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center">
            <RefreshCw size={16} className="text-orange-600 mr-2" />
            <span className="text-sm text-orange-700 font-medium">
              {offlineQueue.length} upload{offlineQueue.length > 1 ? 's' : ''} queued for when connection improves
            </span>
          </div>
          {isOnline && (
            <button
              onClick={() => {
                // Trigger manual queue processing
                const processQueue = async () => {
                  const queuedUploads = [...offlineQueue];
                  setOfflineQueue([]);

                  for (const queuedUpload of queuedUploads) {
                    try {
                      await queuedUpload.operation();
                      toast.success(`Uploaded ${queuedUpload.filename}`);
                    } catch (error) {
                      console.error('Failed to process queued upload:', error);
                      toast.error(`Failed to upload ${queuedUpload.filename}`);
                    }
                  }
                };
                processQueue();
              }}
              className="text-sm bg-orange-600 text-white px-3 py-1 rounded-md hover:bg-orange-700 transition-colors"
            >
              Process Now
            </button>
          )}
        </div>
      )}

      {/* Batch Progress */}
      {batchProgress.active && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium text-gray-700">Processing Batch...</h4>
              {isOnline ? (
                <Wifi size={14} className="text-green-500" />
              ) : (
                <WifiOff size={14} className="text-red-500" />
              )}
            </div>
            <span className="text-sm text-gray-600">
              {batchProgress.current} of {batchProgress.total}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-200 rounded-full h-3">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                style={{ width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 min-w-[3rem]">
              {Math.round((batchProgress.current / batchProgress.total) * 100)}%
            </span>
          </div>
          <div className="text-xs text-gray-500 text-center">
            Network Quality: {networkQuality === 'good' ? '🟢 Good' : networkQuality === 'slow' ? '🟡 Slow' : '🔴 Poor'}
            {networkQuality !== 'good' && ' - Using smaller batches'}
          </div>
        </div>
      )}

      {/* Individual Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && !batchProgress.active && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <h4 className="text-sm font-medium text-gray-700">Uploading...</h4>
            {isOnline ? (
              <Wifi size={14} className="text-green-500" />
            ) : (
              <WifiOff size={14} className="text-red-500" />
            )}
          </div>
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-cyan-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 min-w-[3rem]">{progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Upload Errors */}
      {Object.keys(uploadErrors).length > 0 && Object.values(uploadErrors).some(error => error && error.message) && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-red-700">Upload Errors:</h4>
          {Object.entries(uploadErrors).map(([fileId, error]) => {
            if (!error) return null;
            const image = images.find(img => img.id === fileId);
            const attempts = retryAttempts[fileId] || 0;

            return (
              <div key={fileId} className="flex items-start space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-800">
                    {image?.filename || 'Unknown file'}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {error.message}
                  </p>
                  {attempts > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Retry attempt {attempts}/3
                    </p>
                  )}
                </div>
                {attempts < 3 && (
                  <button
                    onClick={() => retryUpload(fileId)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex-shrink-0"
                    title="Retry upload"
                  >
                    <RefreshCw size={14} />
                    <span>Retry</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected Images:</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((image) => (
              <div key={image.id} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                  {image.preview || image.url ? (
                    <img
                      src={getAbsoluteImageUrl(image.preview || image.url)}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Try fallback URL if available
                        const fallbackUrl = image.url !== image.preview ? (image.url || image.preview) : null;
                        if (fallbackUrl && e.target.src !== getAbsoluteImageUrl(fallbackUrl)) {
                          e.target.src = getAbsoluteImageUrl(fallbackUrl);
                        } else {
                          // Show fallback icon
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}

                  {/* Fallback for failed image loads */}
                  <div className="w-full h-full flex items-center justify-center text-gray-400 hidden">
                    <ImageIcon size={24} />
                  </div>
                </div>

                {/* Image Info Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                    {!disabled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(image.id);
                        }}
                        className="bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 transition-colors"
                        title="Remove image"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Temporary indicator */}
                {image.temporary && (
                  <div className="absolute top-1 right-1">
                    <span className="text-xs text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">
                      New
                    </span>
                  </div>
                )}

                {/* File info */}
                <div className="mt-1 text-xs text-gray-500 truncate" title={image.filename}>
                  {image.filename}
                </div>
                <div className="text-xs text-gray-400">
                  {(image.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {images.length === 0 && disabled && (
        <div className="flex items-center justify-center text-gray-400 py-4">
          <AlertCircle size={16} className="mr-2" />
          <span className="text-sm">Image upload disabled</span>
        </div>
      )}

      {/* Camera Modal */}
      {showCameraModal && (
        <CameraModal
          isOpen={showCameraModal}
          onClose={() => setShowCameraModal(false)}
          onCapture={handleCameraCapture}
        />
      )}
    </div>
  );
};

export default AssetImageUpload;